import { useMemo } from 'react';
import type { RecentRuleEvaluation } from '@amzn/helianthus-arbiter-client';
import { useArbiterClient } from '../context/ArbiterClientContext';
import { usePaginatedApi } from './usePaginatedApi';

/**
 * Pages fetched per load. A rule's result history is unbounded, unlike its version list, so this
 * must stop and leave the next batch to paging rather than drain the whole rule. Two pages of the
 * service default fill the table; deduping can make a page yield fewer rows than it returned, so
 * one page on its own can look short.
 */
const PAGES_PER_LOAD = 2;

interface UseRecentRuleEvaluationsResult {
  /** Newest first, one entry per evaluation. */
  evaluations: RecentRuleEvaluation[];
  loading: boolean;
  fetchingMore: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => void;
  refetch: () => void;
}

/**
 * Collapses the entries the service reports for one evaluation into a single row.
 *
 * One evaluation can arrive on two consecutive pages: the service resumes paging from a result
 * row, not from an evaluation, so an evaluation whose rows straddle a page boundary is reported by
 * both pages. Its own docs tell callers stitching pages into one list to dedupe by evaluation id,
 * and usePaginatedApi accumulates every page into one array.
 *
 * The two entries' finding counts are NOT added, because the rows behind them overlap. The service
 * resumes from the row that filled the previous page's limit, and it had already counted every row
 * after that one on the DynamoDB page it was reading, so the next page re-reads and re-counts them.
 * Adding the two therefore reports more findings than the rule produced.
 *
 * Each count is a lower bound on its own, since the service only counts the rows it read for that
 * page, so the larger of the two is the best lower bound available and cannot overstate the total.
 */
function dedupeByEvaluation(entries: RecentRuleEvaluation[]): RecentRuleEvaluation[] {
  // Insertion order preserves the service's newest-first order; re-setting a key keeps its slot.
  const byId = new Map<string, RecentRuleEvaluation>();

  for (const entry of entries) {
    // Required by the model, so this drops nothing in practice; a row without an id cannot be
    // keyed, linked or copied, so the table can then rely on it.
    const id = entry.evaluationId;
    if (!id) continue;

    // The first sighting is the newest, since pages arrive newest-first, so it keeps its timestamp
    // and rule version; only the finding count is reconciled. Absent on both sides stays unknown,
    // which is not the same answer as zero.
    const seen = byId.get(id);
    const findingCount =
      seen?.findingCount == null && entry.findingCount == null
        ? undefined
        : Math.max(seen?.findingCount ?? 0, entry.findingCount ?? 0);
    byId.set(id, seen ? { ...seen, findingCount } : entry);
  }

  return [...byId.values()];
}

/** The newest evaluations that stored a result for one rule, deduped across pages. */
export function useRecentRuleEvaluations(
  ruleName: string | undefined,
): UseRecentRuleEvaluationsResult {
  const client = useArbiterClient();

  const { data, ...paging } = usePaginatedApi(
    (fetchParams) =>
      client.listRecentEvaluationsForRule(fetchParams).then((r) => ({
        items: r.evaluations ?? [],
        continuationToken: r.continuationToken,
      })),
    { ruleName: ruleName ?? '' },
    { enabled: !!ruleName, maxPages: PAGES_PER_LOAD },
  );

  const evaluations = useMemo(() => dedupeByEvaluation(data), [data]);

  return { ...paging, evaluations };
}
